import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  Typography,
  Autocomplete,
  Chip
} from '@mui/material';
import { Class as ClassIcon } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { SectionInstance } from '../../types/sessions';
import { sectionInstancesApi } from '../../services/sessions/sectionInstancesApi';

interface SectionFormDialogProps {
  open: boolean;
  sessionId: string;
  classId: string;
  className?: string;       // for the dialog subtitle
  initial?: SectionInstance | null;
  /**
   * Section names that already exist in OTHER classes in this same session.
   * Shown as suggestions in the name field — pick to reuse, or type a new one.
   * Deduped + sorted by the parent; we just render.
   */
  nameSuggestions?: string[];
  onClose: () => void;
  onSaved: (section: SectionInstance) => void;
}

interface FormValues {
  name: string;
  room: string;
  capacity: string;
}

const validationSchema = yup.object({
  name: yup.string().trim().required('Section name is required').max(40, 'Keep it short'),
  room: yup.string().trim().max(40),
  capacity: yup.string().matches(/^$|^[1-9]\d*$/, 'Must be a positive number')
});

const SectionFormDialog: React.FC<SectionFormDialogProps> = ({
  open, sessionId, classId, className, initial, nameSuggestions = [], onClose, onSaved
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = Boolean(initial);

  const initialValues = useMemo<FormValues>(() => ({
    name: initial?.name ?? '',
    room: initial?.room ?? '',
    capacity: initial?.capacity != null ? String(initial.capacity) : ''
  }), [initial]);

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          name: values.name.trim(),
          room: values.room.trim() || undefined,
          capacity: values.capacity ? Number(values.capacity) : undefined
        };
        const saved = isEdit
          ? await sectionInstancesApi.update(initial!._id, payload)
          : await sectionInstancesApi.create({ sessionId, classId, ...payload });
        enqueueSnackbar(isEdit ? 'Section updated' : 'Section added', { variant: 'success' });
        onSaved(saved);
        onClose();
      } catch (err: any) {
        enqueueSnackbar(err?.message || 'Could not save section', { variant: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={formik.handleSubmit} noValidate>
        <DialogTitle component="div" sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <ClassIcon color="primary" fontSize="small" />
            <Typography variant="h6" fontWeight={600}>
              {isEdit ? 'Edit section' : 'Add section'}
            </Typography>
          </Stack>
          {className && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {className} · this session
            </Typography>
          )}
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2.25}>
            <Autocomplete<string, false, false, true>
              freeSolo
              autoSelect
              options={nameSuggestions}
              value={formik.values.name}
              onChange={(_, v) => formik.setFieldValue('name', v ?? '')}
              onInputChange={(_, v) => formik.setFieldValue('name', v)}
              onBlur={() => formik.setFieldTouched('name', true)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Section name"
                  placeholder="A · Science · Morning"
                  autoFocus
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={
                    (formik.touched.name && formik.errors.name) ||
                    (nameSuggestions.length > 0
                      ? 'Pick a section already in use this session, or type a new name.'
                      : 'Shown across the app — short labels work best.')
                  }
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option}>
                  <Chip label={option} size="small" variant="outlined" sx={{ mr: 1, fontWeight: 500 }} />
                  <Typography variant="body2" color="text.secondary">
                    Reuse this name
                  </Typography>
                </li>
              )}
              filterOptions={(options, state) => {
                const input = state.inputValue.trim().toLowerCase();
                if (!input) return options;
                return options.filter(o => o.toLowerCase().includes(input));
              }}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                name="room"
                label="Room"
                placeholder="e.g. R-201"
                value={formik.values.room}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.room && Boolean(formik.errors.room)}
                helperText={formik.touched.room && formik.errors.room}
                fullWidth
              />
              <TextField
                name="capacity"
                label="Capacity"
                placeholder="Optional"
                value={formik.values.capacity}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.capacity && Boolean(formik.errors.capacity)}
                helperText={(formik.touched.capacity && formik.errors.capacity) || 'Soft cap (warns; never blocks)'}
                fullWidth
              />
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose} disabled={formik.isSubmitting}>Cancel</Button>
          <Button type="submit" variant="contained" disableElevation disabled={formik.isSubmitting}>
            {isEdit ? 'Save changes' : 'Add section'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SectionFormDialog;
